import { detectIdentifierType } from "@nyntax/contracts/schemas/auth";
import { canonicalBdPhone } from "@nyntax/contracts/validators/phone";

/**
 * Sign-in handoff for the Academy (ADR 0012). The Academy host has its own
 * host-only session cookie, so the Learner signs in here, against the shared
 * ClassTablet accounts: the form talks to the API's Better Auth endpoints
 * through the same-origin `/api/auth` proxy (next.config.ts), which lets the
 * API set the cookie on this host. No account data lives in the Academy.
 */

const MAX_REDIRECT_LENGTH = 512;

/** Raised when the sign-in service cannot be reached or answers with an unexpected status. */
export class SignInError extends Error {
  /**
   * Creates a sign-in failure with a message and an optional underlying cause.
   *
   * @param message - Human-readable description of the failure.
   * @param options - Optional extra data; `cause` keeps the original error.
   */
  constructor(message: string, options?: { cause?: unknown }) {
    super(message, options);
    this.name = "SignInError";
  }
}

/** Outcome of a sign-in attempt that reached the service: accepted, wrong credentials, or throttled. */
export type SignInResult = "ok" | "invalid" | "rate-limited";

/**
 * A redirect target that stays on this site: a relative path only. Rejects
 * absolute and protocol-relative URLs and anything with `@`, so the sign-in
 * page can never be used to send a Learner to another host.
 *
 * @param redirect - The raw `redirect` value from the URL, or null when absent.
 * @param fallback - The path to return when the value is missing or unsafe.
 * @returns The redirect path when it is safe to follow, otherwise `fallback`.
 */
export const getSafeRedirect = (
  redirect: string | null | undefined,
  fallback: string
): string => {
  if (redirect === null || redirect === undefined || redirect === "") {
    return fallback;
  }

  const containsUnsafeSequence =
    redirect.includes("//") ||
    redirect.includes("@") ||
    redirect.includes("\\");

  const isUnsafe =
    redirect.length > MAX_REDIRECT_LENGTH ||
    !redirect.startsWith("/") ||
    containsUnsafeSequence;

  return isUnsafe ? fallback : redirect;
};

/**
 * The sign-in page for a locale, remembering where to return to afterwards.
 *
 * @param locale - The active locale segment used as the route prefix.
 * @param returnTo - Optional path to return to after sign-in; unsafe values are dropped.
 * @returns The sign-in URL, with a `redirect` query parameter when a safe path was given.
 */
export const signInHref = (locale: string, returnTo?: string): string => {
  const safe = getSafeRedirect(returnTo, "");

  return safe === ""
    ? `/${locale}/sign-in`
    : `/${locale}/sign-in?redirect=${encodeURIComponent(safe)}`;
};

/**
 * The Better Auth call for an email or Bangladeshi phone number plus password.
 *
 * @param identifier - An email address or a Bangladeshi phone number typed by the Learner.
 * @param password - The account password, sent as typed.
 * @returns The sign-in endpoint path and the JSON body to POST to it.
 */
export const buildSignInRequest = (identifier: string, password: string) => {
  const trimmed = identifier.trim();

  const phone =
    detectIdentifierType(trimmed) === "phone"
      ? canonicalBdPhone(trimmed)
      : null;

  if (phone !== null && phone.length > 0) {
    return {
      path: "/api/auth/sign-in/phone-number",
      body: { phoneNumber: phone, password },
    };
  }

  return {
    path: "/api/auth/sign-in/email",
    body: { email: trimmed.toLowerCase(), password },
  };
};

const HTTP_BAD_REQUEST = 400;
const HTTP_UNAUTHORIZED = 401;
const HTTP_FORBIDDEN = 403;
const HTTP_TOO_MANY = 429;

/**
 * Signs in. Wrong credentials are an answer ("invalid"); an outage throws.
 *
 * @param options - The sign-in inputs.
 * - `fetcher`: The fetch implementation used for the request; defaults to the global `fetch`.
 * - `identifier`: An email address or Bangladeshi phone number.
 * - `password`: The account password.
 * @returns "ok" on success, "invalid" for rejected credentials, or "rate-limited" when throttled.
 * @throws {@link SignInError} when the service is unreachable or answers with an unexpected status.
 */
export const signIn = async ({
  fetcher = fetch,
  identifier,
  password,
}: {
  fetcher?: typeof fetch;
  identifier: string;
  password: string;
}): Promise<SignInResult> => {
  const { path, body } = buildSignInRequest(identifier, password);
  let response: Response;

  try {
    response = await fetcher(path, {
      method: "POST",
      headers: {
        accept: "application/json",
        "content-type": "application/json",
      },
      body: JSON.stringify(body),
      cache: "no-store",
    });
  } catch (error) {
    throw new SignInError("The sign-in service is unreachable", { cause: error });
  }

  if (response.ok) {
    return "ok";
  }

  if (response.status === HTTP_TOO_MANY) {
    return "rate-limited";
  }

  if (
    response.status === HTTP_BAD_REQUEST ||
    response.status === HTTP_UNAUTHORIZED ||
    response.status === HTTP_FORBIDDEN
  ) {
    return "invalid";
  }

  throw new SignInError(`The sign-in service answered ${response.status}`);
};

/** Outcome of a sign-up attempt that reached the service. */
export type SignUpResult = "ok" | "exists" | "invalid" | "rate-limited";

const HTTP_UNPROCESSABLE = 422;

/**
 * The Better Auth call that creates an account. A phone number becomes the
 * account's phone with a placeholder email (the same convention as the
 * ClassTablet dashboard sign-up), so one account works on both sites.
 *
 * @param name - The person's full name.
 * @param identifier - An email address or a Bangladeshi phone number.
 * @param password - The password, sent as typed.
 * @returns The sign-up endpoint path and the JSON body to POST to it.
 */
export const buildSignUpRequest = (
  name: string,
  identifier: string,
  password: string
) => {
  const trimmed = identifier.trim();

  const phone =
    detectIdentifierType(trimmed) === "phone"
      ? canonicalBdPhone(trimmed)
      : null;

  if (phone !== null && phone.length > 0) {
    return {
      path: "/api/auth/sign-up/email",
      body: {
        name: name.trim(),
        email: `${phone}@phone.classtablet.com`,
        password,
        phoneNumber: phone,
      },
    };
  }

  return {
    path: "/api/auth/sign-up/email",
    body: { name: name.trim(), email: trimmed.toLowerCase(), password },
  };
};

/**
 * Creates an account and signs it in (the API sets the session cookie on this
 * host through the same-origin proxy). Bad input and duplicates are answers;
 * an outage throws.
 *
 * @param options - The sign-up inputs.
 * - `fetcher`: The fetch implementation; defaults to the global `fetch`.
 * - `identifier`: An email address or Bangladeshi phone number.
 * - `name`: The person's full name.
 * - `password`: The password.
 * @returns "ok" on success, "exists" for a taken email or phone, "invalid" for rejected input, or "rate-limited".
 * @throws {@link SignInError} when the service is unreachable or answers unexpectedly.
 */
export const signUp = async ({
  fetcher = fetch,
  identifier,
  name,
  password,
}: {
  fetcher?: typeof fetch;
  identifier: string;
  name: string;
  password: string;
}): Promise<SignUpResult> => {
  const { path, body } = buildSignUpRequest(name, identifier, password);
  let response: Response;

  try {
    response = await fetcher(path, {
      method: "POST",
      headers: {
        accept: "application/json",
        "content-type": "application/json",
      },
      body: JSON.stringify(body),
      cache: "no-store",
    });
  } catch (error) {
    throw new SignInError("The sign-up service is unreachable", {
      cause: error,
    });
  }

  if (response.ok) {
    return "ok";
  }

  if (response.status === HTTP_TOO_MANY) {
    return "rate-limited";
  }

  if (response.status === HTTP_UNPROCESSABLE) {
    return "exists";
  }

  if (response.status === HTTP_BAD_REQUEST) {
    return "invalid";
  }

  throw new SignInError(`The sign-up service answered ${response.status}`);
};

/**
 * Ends the session on this host.
 *
 * @param fetcher - The fetch implementation; defaults to the global `fetch`.
 * @returns Resolves once the API has cleared the session cookie.
 * @throws {@link SignInError} when the service is unreachable.
 */
export const signOut = async (fetcher: typeof fetch = fetch): Promise<void> => {
  try {
    await fetcher("/api/auth/sign-out", {
      method: "POST",
      headers: { accept: "application/json", "content-type": "application/json" },
      body: "{}",
      cache: "no-store",
    });
  } catch (error) {
    throw new SignInError("The sign-out service is unreachable", {
      cause: error,
    });
  }
};

/**
 * Asks Better Auth for the Google consent address that signs the person in (or
 * creates their account). The caller sends the browser there. Every return
 * address is a same-site path the caller has already checked, so Google can
 * never bounce the person to another host.
 *
 * @param options - Where to land afterwards, on success, on failure, and for a brand-new account.
 * - `callbackURL`: a checked same-site path for an existing account.
 * - `errorCallbackURL`: a same-site path to return to when Google or the sign-in fails.
 * - `newUserCallbackURL`: a same-site path for an account created by this sign-in.
 * - `fetcher`: The fetch implementation; defaults to the global `fetch`.
 * @returns The Google address, or null when Google sign-in could not start.
 */
export const startGoogleSignIn = async ({
  callbackURL,
  errorCallbackURL,
  fetcher = fetch,
  newUserCallbackURL,
}: {
  callbackURL: string;
  errorCallbackURL: string;
  fetcher?: typeof fetch;
  newUserCallbackURL?: string;
}): Promise<string | null> => {
  try {
    const response = await fetcher("/api/auth/sign-in/social", {
      method: "POST",
      headers: {
        accept: "application/json",
        "content-type": "application/json",
      },
      body: JSON.stringify({
        provider: "google",
        callbackURL,
        errorCallbackURL,
        ...(newUserCallbackURL === undefined ? {} : { newUserCallbackURL }),
      }),
      cache: "no-store",
    });

    if (!response.ok) {
      return null;
    }

    const body: unknown = await response.json().catch(() => null);

    if (typeof body === "object" && body !== null && "url" in body) {
      const { url } = body as { url: unknown };

      return typeof url === "string" ? url : null;
    }

    return null;
  } catch {
    return null;
  }
};
